import {
   ApiResponse,
   createApiRequest,
   getHeaderFromSessionStorage,
   Header,
} from "@/api"
import { AlertResponse } from "@/api/AlertsService"
import { InmateCustodyStatus } from "@/api/InmatesService"
import { API_URL, Endpoints } from "@/constants/endpoints"
import { HttpMethods } from "@/constants/globals"
import { SensorForm } from "@/types/sensor"

export enum SensorType {
   STATIC = "static",
   WEARABLE = "wearable",
   ANCHOR = "anchor",
   DOOR = "door",
}

export interface SensorResponse {
   id: string
   level: string
   macAddress: string
   serialNumber: string
   type: SensorType
}

export enum WearableSensorStatus {
   IDLE = "Idle",
   WEARING = "Wearing",
   CHARGING = "Charging",
}

export interface WearableSensorInmate {
   id: string
   customIdentifier: string
   externalId: string | null
   firstName: string
   middleName: string | null
   lastName: string
   inmateNumber: string | null
   bookingNumber: string | null
   housingUnit: string | null
   housingBlock: string | null
   housingCell: string | null
   custodyStatus: InmateCustodyStatus | null
}

export interface WearableSensorResponse {
   id: string
   serialNumber: string
   macAddress: string
   type: SensorType
   level: string
   status: WearableSensorStatus
   inmate: WearableSensorInmate | null
   batteryPercentage: number | null
   alarm: AlertResponse | null
}

export interface WearableChargingStatusResponse {
   id: string
   macAddress: string
   charging: boolean
   batteryPercentage: number | null
}

export interface SensorsParams {
   type?: SensorType
   hasInmate?: boolean
   serialNumber?: string
   macAddress?: string
}

export async function requestSensors(
   params?: SensorsParams
): Promise<ApiResponse<SensorResponse[]>> {
   const url = new URL(`${API_URL}${Endpoints.SENSORS}`)
   if (params?.type !== undefined) {
      url.searchParams.set("type", params.type)
   }
   if (params?.hasInmate !== undefined) {
      url.searchParams.set("has_inmate", String(params.hasInmate))
   }
   if (params?.serialNumber) {
      url.searchParams.set("serial_number", params.serialNumber)
   }
   if (params?.macAddress) {
      url.searchParams.set("mac_address", params.macAddress)
   }
   const response = await createApiRequest(
      url.toString(),
      HttpMethods.GET,
      undefined,
      getHeaderFromSessionStorage(Header.FACILITY_ID)
   )
   return response
}

export async function requestSensorsByType(
   type: SensorType
): Promise<ApiResponse<WearableSensorResponse[]>> {
   const response = await createApiRequest(
      `${API_URL}${Endpoints.SENSORS}${type}/`,
      HttpMethods.GET,
      undefined,
      getHeaderFromSessionStorage(Header.FACILITY_ID)
   )
   return response
}

export async function requestWearableChargingStatus(): Promise<
   ApiResponse<WearableChargingStatusResponse[]>
> {
   const response = await createApiRequest(
      `${API_URL}${Endpoints.WEARABLES_CHARGING_STATUS}`,
      HttpMethods.GET,
      undefined,
      getHeaderFromSessionStorage(Header.FACILITY_ID)
   )
   return response
}

export async function requestCreateSensor(
   sensorForm: Partial<SensorForm>[]
): Promise<ApiResponse<SensorResponse[]>> {
   const response = await createApiRequest(
      `${API_URL}${Endpoints.SENSORS}`,
      HttpMethods.POST,
      sensorForm,
      getHeaderFromSessionStorage(Header.FACILITY_ID)
   )
   return response
}

export async function requestUpdateSensor(
   sensorId: string,
   sensorForm: Partial<SensorForm>
): Promise<ApiResponse<SensorResponse>> {
   const response = await createApiRequest(
      `${API_URL}${Endpoints.SENSORS}${sensorId}/`,
      HttpMethods.PATCH,
      sensorForm,
      getHeaderFromSessionStorage(Header.FACILITY_ID)
   )
   return response
}

export async function requestDeleteSensor(
   sensorId: string
): Promise<ApiResponse<void>> {
   const response = await createApiRequest(
      `${API_URL}${Endpoints.SENSORS}${sensorId}/`,
      HttpMethods.DELETE,
      undefined,
      getHeaderFromSessionStorage(Header.FACILITY_ID)
   )
   return response
}
